/* A deliberate gift choice in the bag. Shopify owns inventory, prices and discounts. */
(() => {
  'use strict';
  if (window.DivineCartGifts) return;
  const rules = { b2g1: { buys: 2, gets: 1 }, b2g2: { buys: 2, gets: 2 }, b3g2: { buys: 3, gets: 2 } };
  const widgets = new Set();
  let checkSignature = '', checkPromise = Promise.resolve();
  let cart = null, cartUrl = null, readPromise = null, busy = false, uncertain = false, revision = 0, readVersion = 0, serial = 0;
  const validCart = value => value && Array.isArray(value.items) && Number.isSafeInteger(value.total_price) && Number.isSafeInteger(value.item_count);
  const integer = value => Number.isSafeInteger(value) && value >= 0;
  const eligibleProducts = data => (data.products || []).filter(product => product.eligible && !product.gift_card && !product.requiresSellingPlan);
  function savings(lots, free) {
    let result = 0;
    for (const lot of lots.slice().sort((a, b) => a.price - b.price)) {
      const quantity = Math.min(free, lot.quantity); result += quantity * lot.price; free -= quantity;
      if (!free) break;
    }
    return result;
  }
  function evaluate(value, data, additions = []) {
    if (!validCart(value)) throw new Error('Your bag could not be checked. Please refresh it.');
    const rule = rules[data.mode];
    if (!rule) return { count: 0, covered: 0, paid: 0, unlocked: 0, needed: 0, savings: 0, due: 0, extraSavings: 0 };
    const ids = new Set(eligibleProducts(data).map(product => String(product.id)));
    const lots = [];
    for (const line of value.items) {
      if (!ids.has(String(line.product_id)) || line.gift_card || line.selling_plan_allocation || line.selling_plan) continue;
      if (!integer(line.quantity) || !line.quantity || !integer(line.original_line_price) || line.original_line_price % line.quantity) throw new Error('Your bag prices need to be refreshed.');
      lots.push({ quantity: line.quantity, price: line.original_line_price / line.quantity });
    }
    const count = lots.reduce((sum, lot) => sum + lot.quantity, 0);
    const covered = Math.floor(count / (rule.buys + rule.gets)) * rule.gets;
    const paid = count - covered;
    const unlocked = Math.min(rule.gets, Math.max(0, Math.floor(paid / rule.buys) * rule.gets - covered));
    const currentSavings = savings(lots, covered);
    const nextLots = lots.concat(additions.map(variant => ({ quantity: 1, price: variant.price })));
    const nextFree = Math.floor((count + additions.length) / (rule.buys + rule.gets)) * rule.gets;
    const extraSavings = savings(nextLots, nextFree) - currentSavings;
    const extraPrice = additions.reduce((sum, variant) => sum + variant.price, 0);
    return { count, covered, paid, unlocked, needed: unlocked ? 0 : (Math.floor(paid / rule.buys) + 1) * rule.buys - paid, savings: currentSavings, extraSavings, due: Math.max(0, extraPrice - extraSavings) };
  }
  function fingerprint(value) {
    return JSON.stringify(value.items.map(line => [line.key || line.variant_id || line.id, line.quantity, line.original_line_price, !!(line.selling_plan_allocation || line.selling_plan)]));
  }
  function verifySnapshot() {
    const widget = widgets.values().next().value;
    const checker = window.DivineOfferCartCheck;
    if (!widget || widget.dataset.reviewOffer === 'true' || !checker || !validCart(cart)) return Promise.resolve();
    const eligibleProductIds = eligibleProducts(widget.data).map(product => product.id);
    const config = { mode: widget.data.mode, eligibleProductIds, giftProductIds: eligibleProductIds };
    const signature = JSON.stringify([config, cart.currency, cart.total_price, cart.items.map(line => [line.product_id, line.variant_id || line.id, line.quantity, line.original_line_price, line.final_line_price, !!line.gift_card, !!(line.selling_plan_allocation || line.selling_plan)])]);
    if (signature === checkSignature) { checker.paint(); return checkPromise; }
    checkSignature = signature;
    checkPromise = Promise.resolve(checker.verify(config, cart));
    return checkPromise;
  }
  function adopt(value) {
    if (!validCart(value)) return false;
    cart = value; revision++;
    for (const widget of widgets) widget.render();
    verifySnapshot().catch(() => {});
    return true;
  }
  async function refresh(value, force = false) {
    if (validCart(value)) { readVersion++; uncertain = false; adopt(value); return cart; }
    if (readPromise && !force) return readPromise;
    const started = revision;
    const version = ++readVersion;
    const request = (async () => {
      const root = window.Shopify?.routes?.root || '/';
      const url = cartUrl || root.replace(/\/?$/, '/') + 'cart';
      const response = await fetch(url.replace(/\.js$/, '') + '.js', { method: 'GET', credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('We couldn’t refresh your bag. Please try again.');
      const next = await response.json();
      if (!validCart(next)) throw new Error('We couldn’t read your bag. Please refresh the page.');
      if (version === readVersion && started === revision) { uncertain = false; adopt(next); }
      else if (readPromise && readPromise !== request) return await readPromise;
      return cart;
    })();
    readPromise = request;
    try { return await request; } finally { if (readPromise === request) readPromise = null; }
  }
  function renderAll() { for (const widget of widgets) widget.render(); }
  function create(tag, className, text) {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (text !== undefined) element.textContent = text;
    return element;
  }
  function safeImage(url) { return typeof url === 'string' && /^(https:\/\/|\/\/|\/[^/])/.test(url) ? url : ''; }
  class DivineCartGifts extends HTMLElement {
    connectedCallback() {
      if (this.initialized) {
        if (!this.data || !rules[this.data.mode] || !this.products?.length) return;
        widgets.add(this); this.render(); verifySnapshot().catch(() => {}); return;
      }
      this.initialized = true;
      try { this.data = JSON.parse(this.querySelector('[data-cart-gift-data]').textContent); }
      catch (_) { this.hidden = true; return; }
      if (!rules[this.data.mode] || !eligibleProducts(this.data).length) { this.hidden = true; return; }
      this.uid = 'dd-cart-gift-' + (++serial);
      this.rows = []; this.opened = false;
      this.products = eligibleProducts(this.data).sort((first, second) => Number(!!second.recommended) - Number(!!first.recommended) || Number(!!second.giftEligible) - Number(!!first.giftEligible));
      cartUrl = this.dataset.cartUrl || cartUrl;
      if (!cart && validCart(this.data.cart)) cart = this.data.cart;
      widgets.add(this);
      this.querySelector('[data-gift-open]')?.addEventListener('click', () => this.openPicker());
      this.querySelector('[data-gift-add]')?.addEventListener('click', () => this.addGifts());
      this.addEventListener('change', event => {
        if (event.target.matches('[data-gift-product], [data-gift-variant]')) event.stopPropagation();
      });
      this.render();
      verifySnapshot().catch(() => {});
      if (!cart) refresh().catch(error => this.error(error.message));
    }
    disconnectedCallback() { widgets.delete(this); }
    money(value) {
      try { return new Intl.NumberFormat(this.data.locale || document.documentElement.lang || 'en', { style: 'currency', currency: cart?.currency || this.data.currency || 'USD' }).format(value / 100); }
      catch (_) { return (value / 100).toFixed(2); }
    }
    text(selector, value) { const element = this.querySelector(selector); if (element && element.textContent !== value) element.textContent = value; }
    error(message = '') { this.text('[data-gift-error]', message); const element = this.querySelector('[data-gift-error]'); if (element) element.hidden = !message; }
    state() { return evaluate(cart, this.data); }
    choose(row) {
      const product = this.products.find(item => String(item.id) === row.product.value);
      const variant = product?.variants?.find(item => String(item.id) === row.variant.value);
      return { product, variant };
    }
    selection() {
      const state = this.state();
      if (!state.unlocked) throw new Error('Add the qualifying jewelry to your bag before choosing your next piece.');
      if (this.rows.length !== state.unlocked) throw new Error('Your bag changed. Please review your choices.');
      const selected = this.rows.map(row => this.choose(row));
      for (const { product, variant } of selected) {
        if (!product || !variant) throw new Error('Choose a piece and an option for each selection.');
        if (!variant.available || !integer(variant.price)) throw new Error('That option is unavailable. Please choose another.');
        if (product.requiresSellingPlan || product.variantCount > 250) throw new Error('Please choose a different piece.');
      }
      return { selected, ...evaluate(cart, this.data, selected.map(item => item.variant)) };
    }
    makeRow(index) {
      const element = create('div', 'dd-cart-gifts__row');
      const image = create('img', 'dd-cart-gifts__image'); image.alt = ''; image.width = 64; image.height = 80; image.loading = 'lazy'; image.hidden = true;
      const fields = create('div', 'dd-cart-gifts__fields');
      const productLabel = create('label', 'dd-cart-gifts__label', 'Piece ' + (this.state().count + index + 1));
      const product = create('select', 'dd-cart-gifts__select'); product.id = this.uid + '-product-' + index; product.dataset.giftProduct = ''; productLabel.htmlFor = product.id;
      const empty = create('option', '', 'Choose qualifying jewelry'); empty.value = ''; product.append(empty);
      for (const item of this.products) {
        const option = create('option', '', item.title + (item.recommended ? ' · Most popular: 13 mm Gold & White' : '')); option.value = String(item.id);
        option.disabled = item.variantCount > 250 || !(item.variants || []).some(variant => variant.available);
        product.append(option);
      }
      const variantLabel = create('label', 'dd-cart-gifts__label', 'Choose your option');
      const variant = create('select', 'dd-cart-gifts__select'); variant.id = this.uid + '-variant-' + index; variant.dataset.giftVariant = ''; variantLabel.htmlFor = variant.id; variant.disabled = true;
      const option = create('option', '', 'Select a piece first'); option.value = ''; variant.append(option);
      const price = create('span', 'dd-cart-gifts__row-price');
      fields.append(productLabel, product, variantLabel, variant, price); element.append(image, fields);
      const row = { element, image, product, variant, price };
      product.addEventListener('change', () => {
        const item = this.products.find(candidate => String(candidate.id) === product.value);
        variant.replaceChildren();
        const placeholder = create('option', '', item ? 'Choose an option' : 'Select a piece first'); placeholder.value = ''; variant.append(placeholder);
        for (const choice of item?.variants || []) {
          const option = create('option', '', (choice.title === 'Default Title' ? 'Standard' : choice.title) + (item?.recommended && choice.title === 'Gold and White' ? ' · Most popular' : '') + ' · ' + this.money(choice.price) + (choice.available ? '' : ' · Sold out'));
          option.value = String(choice.id); option.disabled = !choice.available; variant.append(option);
        }
        variant.disabled = !item;
        // A single default SKU is unambiguous after the customer deliberately chooses its product.
        if (item?.variants?.length === 1 && item.variants[0].available) variant.value = String(item.variants[0].id);
        this.error(); this.render();
      });
      variant.addEventListener('change', () => { this.error(); this.render(); });
      return row;
    }
    render() {
      if (!this.data || !rules[this.data.mode]) return;
      this.hidden = false;
      let state;
      try { state = this.state(); } catch (_) {
        this.text('[data-gift-heading]', 'Your jewelry set'); this.text('[data-gift-copy]', 'Refresh your bag to check your offer eligibility.');
        for (const element of this.querySelectorAll('[data-gift-earned], [data-gift-next], [data-gift-progress]')) element.hidden = true;
        const open = this.querySelector('[data-gift-open]'), add = this.querySelector('[data-gift-add]'), picker = this.querySelector('[data-gift-picker]');
        if (open) open.disabled = true; if (add) add.disabled = true; if (picker) picker.hidden = true;
        return;
      }
      const rule = rules[this.data.mode];
      if (!state.unlocked) { this.opened = false; this.rows.forEach(row => row.element.remove()); this.rows = []; }
      while (this.rows.length > state.unlocked) this.rows.pop().element.remove();
      while (this.rows.length < state.unlocked) {
        const row = this.makeRow(this.rows.length); this.rows.push(row); this.querySelector('[data-gift-rows]')?.append(row.element);
      }
      this.dataset.state = state.unlocked ? 'unlocked' : state.covered ? 'complete' : 'progress';
      this.text('[data-gift-heading]', this.dataset.reviewOffer === 'true' && state.unlocked ? 'Preview your next piece' : state.unlocked ? (state.unlocked === 1 ? 'Your next piece is free' : 'Your next ' + state.unlocked + ' pieces are free') : state.covered ? (state.covered === 1 ? 'Your free piece is in your bag' : 'Your free pieces are in your bag') : 'Buy ' + rule.buys + ', Get ' + rule.gets + ' Free');
      const earned = this.querySelector('[data-gift-earned]');
      if (earned) {
        earned.hidden = !state.covered;
        this.text('[data-gift-earned]', state.covered ? 'You’re getting ' + state.covered + ' free ' + (state.covered === 1 ? 'piece' : 'pieces') + '. It’s marked FREE in your bag above.' : '');
      }
      this.text('[data-gift-copy]', this.dataset.reviewOffer === 'true' ? (state.unlocked ? 'Choose another piece to see an example set total. Savings are preview only.' : 'Add ' + state.needed + ' more qualifying ' + (state.needed === 1 ? 'piece' : 'pieces') + ' to preview your set.') : state.unlocked ? 'Add one more piece and the lowest-priced piece in your set is free.' : state.covered === 1 ? 'Want another free piece? Add ' + state.needed + ' more ' + (state.needed === 1 ? 'piece' : 'pieces') + ' and your next one is free too.' : state.covered ? 'Your free pieces are already taken off your total.' : 'Add ' + state.needed + ' more ' + (state.needed === 1 ? 'piece' : 'pieces') + ' and your next one is free.');
      const shop = this.querySelector('[data-gift-shop]'); if (shop) shop.hidden = !!state.unlocked || state.covered >= 2;
      const next = this.querySelector('[data-gift-next]');
      if (next) {
        next.hidden = !state.covered || !state.unlocked;
        this.text('[data-gift-next]', state.covered && state.unlocked ? 'Your bag already qualifies for another piece in the offer.' : '');
      }
      const progress = this.querySelector('[data-gift-progress]');
      if (progress) { progress.hidden = state.covered >= 2; progress.max = rule.buys; progress.value = state.unlocked ? rule.buys : rule.buys - state.needed; progress.setAttribute('aria-label', state.covered ? 'Jewelry toward your second free piece' : 'Jewelry toward your first set offer'); }
      const open = this.querySelector('[data-gift-open]');
      if (open) { open.hidden = !state.unlocked; open.disabled = busy; open.setAttribute('aria-expanded', String(this.opened)); open.textContent = this.opened ? 'Hide choices' : 'Choose my free piece'; }
      const picker = this.querySelector('[data-gift-picker]'); if (picker) picker.hidden = !this.opened || !state.unlocked;
      for (const row of this.rows) {
        const { product, variant } = this.choose(row);
        row.product.disabled = busy; row.variant.disabled = busy || !product;
        const source = safeImage(variant?.image || product?.variants?.find(item => item.image)?.image);
        row.image.hidden = !source; if (source) row.image.src = source;
        row.image.alt = product ? product.title : '';
        row.price.textContent = variant ? 'Regular price ' + this.money(variant.price) : '';
      }
      let selected = null;
      try { selected = this.selection(); } catch (_) { /* An empty choice remains a deliberate action. */ }
      const add = this.querySelector('[data-gift-add]');
      if (add) {
        add.disabled = busy || !selected || uncertain;
        add.textContent = this.dataset.reviewOffer === 'true' ? 'Preview my set' : busy ? 'Adding to your bag…' : selected ? 'Add ' + (state.unlocked > 1 ? 'my pieces' : 'this piece') + ' · +' + this.money(selected.due) + ' est.' : 'Choose a piece';
      }
      this.text('[data-gift-summary]', this.dataset.reviewOffer === 'true' ? (selected ? 'Example set savings: ' + this.money(selected.extraSavings) + '. Checkout will not apply these savings until the discount is enabled.' : 'Choose a piece and option to see the example.') : selected ? 'Estimated additional total: ' + this.money(selected.due) + '. If this piece costs more, a cheaper piece already in your bag may be free instead.' : 'Choose a piece and option to see the estimate.');
      this.text('[data-gift-note]', this.dataset.reviewOffer === 'true' ? 'Review preview only. No pieces are added until a matching automatic discount is live.' : 'For each set, the least expensive qualifying piece receives the discount. Your bag and checkout show Shopify’s actual prices.');
      this.setAttribute('aria-busy', String(busy));
    }
    async openPicker() {
      if (busy) return;
      if (this.opened) { this.opened = false; this.render(); return; }
      this.error(); this.text('[data-gift-status]', 'Checking your bag…');
      try {
        await refresh(undefined, true);
        if (!this.state().unlocked) { this.text('[data-gift-status]', 'Your bag has been updated.'); return; }
        this.opened = true; this.render(); this.rows[0]?.product.focus(); this.text('[data-gift-status]', 'Your piece choices are ready.');
      } catch (error) { this.error(error.message); this.text('[data-gift-status]', ''); }
    }
    sectionPlan() {
      const main = document.getElementById('main-cart-items');
      if (main) {
        const footer = document.getElementById('main-cart-footer');
        return { type: 'page', sections: [
          { id: 'main-cart-items', section: main.dataset.id, selector: '.js-contents' },
          ...(footer ? [{ id: 'main-cart-footer', section: footer.dataset.id, selector: '.js-contents' }] : []),
          { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' }
        ].filter(section => section.section) };
      }
      const drawer = document.querySelector('cart-drawer');
      return drawer?.getSectionsToRender ? { type: 'drawer', drawer, sections: drawer.getSectionsToRender().map(section => ({ ...section, section: section.id })) } : { type: 'redirect', sections: [] };
    }
    renderSections(result, plan) {
      if (plan.type === 'redirect') return false;
      if (plan.sections.some(section => typeof result.sections?.[section.section] !== 'string')) return false;
      if (plan.type === 'drawer') {
        plan.drawer.classList.remove('is-empty');
        plan.drawer.renderContents({ ...result, id: result.items?.[0]?.id, key: result.items?.[0]?.key });
      } else {
        const updates = plan.sections.map(section => {
          const target = document.getElementById(section.id);
          const source = new DOMParser().parseFromString(result.sections[section.section], 'text/html').querySelector(section.selector);
          if (!target || !source) throw new Error('Bag section is incomplete');
          return { target: target.querySelector(section.selector) || target, source };
        });
        for (const { target, source } of updates) target.innerHTML = source.innerHTML;
      }
      return true;
    }
    async addGifts() {
      if (busy || uncertain) return;
      let selection;
      try { selection = this.selection(); } catch (error) { this.error(error.message); return; }
      if (this.dataset.reviewOffer === 'true') {
        this.error('Preview: ' + selection.selected.map(item => item.product.title + ' (' + item.variant.title + ')').join(', ') + '. Example savings ' + this.money(selection.extraSavings) + '. Nothing was added to your bag.');
        return;
      }
      const expectedCart = fingerprint(cart);
      const expectedChoices = JSON.stringify(selection.selected.map(item => item.variant.id));
      const plan = this.sectionPlan();
      let attempted = false, added = false;
      busy = true; this.error(); renderAll();
      try {
        await refresh(undefined, true);
        const current = this.selection();
        if (fingerprint(cart) !== expectedCart || JSON.stringify(current.selected.map(item => item.variant.id)) !== expectedChoices || current.due !== selection.due) throw new Error('Your bag changed. Review your choices and updated total, then add again.');
        const grouped = new Map();
        for (const { variant } of current.selected) {
          const entry = grouped.get(String(variant.id)) || { id: variant.id, quantity: 0, variant };
          entry.quantity++; grouped.set(String(variant.id), entry);
        }
        for (const { id, quantity, variant } of grouped.values()) {
          const existing = cart.items.filter(line => String(line.variant_id || line.id) === String(id)).reduce((sum, line) => sum + line.quantity, 0);
          const total = existing + quantity;
          if (total < (variant.min || 1) || total % (variant.increment || 1)) throw new Error('That piece has a minimum quantity requirement. Please choose another gift.');
          if (variant.max != null && total > variant.max) throw new Error('Your bag would exceed the quantity limit for that piece. Please choose another.');
          if (variant.inventoryManaged && variant.inventoryPolicy !== 'continue' && Number.isFinite(variant.inventoryQuantity) && total > variant.inventoryQuantity) throw new Error('There is not enough stock for that selection. Please choose another gift.');
        }
        const items = [...grouped.values()].map(({ id, quantity }) => ({ id, quantity }));
        attempted = true;
        const response = await fetch(this.dataset.addUrl || (cartUrl || '/cart') + '/add', { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: JSON.stringify({ items, ...(plan.sections.length ? { sections: [...new Set(plan.sections.map(section => section.section))], sections_url: window.location.pathname } : {}) }) });
        const result = await response.json();
        if (!response.ok || result.status >= 400) { attempted = false; throw new Error(result.description || result.message || 'That selection could not be added. Please choose another gift.'); }
        added = true;
        const actualCart = await refresh(undefined, true);
        await verifySnapshot();
        if (!this.renderSections(result, plan)) { window.location.assign(this.dataset.cartUrl || cartUrl || '/cart'); return; }
        // Native cart subscribers refresh their own regions; the response is authoritative for gift widgets immediately.
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') await publish(PUB_SUB_EVENTS.cartUpdate, { source: 'divine-cart-gifts', cartData: actualCart });
        window.DivineOfferCartCheck?.paint();
        this.text('[data-gift-status]', 'Your selection is in your bag. Review your updated total.');
      } catch (error) {
        if (added) { window.location.assign(this.dataset.cartUrl || cartUrl || '/cart'); return; }
        if (attempted) { uncertain = true; this.error('We couldn’t confirm the add. Review or refresh your bag before trying again so you don’t add the same gift twice.'); }
        else this.error(error.message);
      } finally { busy = false; renderAll(); }
    }
  }
  window.DivineCartGifts = { refresh, evaluate, handlesCartChecks: () => widgets.size > 0 };
  if (!customElements.get('divine-cart-gifts')) customElements.define('divine-cart-gifts', DivineCartGifts);
  function start() {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') subscribe(PUB_SUB_EVENTS.cartUpdate, event => {
      if (event?.source === 'divine-cart-gifts') return;
      refresh(validCart(event?.cartData) ? event.cartData : undefined, true).catch(error => { for (const widget of widgets) widget.error(error.message); });
    });
    document.addEventListener('shopify:section:load', () => refresh().catch(() => {}));
    const drawer = document.querySelector('cart-drawer');
    if (drawer && typeof MutationObserver !== 'undefined') {
      let wasOpen = drawer.classList.contains('active');
      new MutationObserver(() => {
        const open = drawer.classList.contains('active');
        if (open && !wasOpen) refresh().catch(error => { for (const widget of widgets) widget.error(error.message); });
        wasOpen = open;
      }).observe(drawer, { attributes: true, attributeFilter: ['class'] });
    }
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true }); else start();
})();
